import Perlin from './perlin';
import createControls from './controls';

// Locked to the values measured off the campaign artwork. Not user-editable.
const STROKE = '#FB002E';
const BACKGROUND = '#E4DED5';
const STROKE_WIDTH = 3;

// Landscape by default; narrow viewports get the same frame stood on end.
const LANDSCAPE = { width: 1920, height: 1080 };
const PORTRAIT = { width: 1080, height: 1920 };
const PORTRAIT_BELOW = 768;

// How far marks may lean off horizontal. Fitted to the campaign artwork's own
// angle distribution, which averages 28-34 degrees off horizontal. Letting the
// noise swing a full turn instead piles the marks into dense convergence
// seams — the single biggest reason a flow field stops looking like this one.
const SPREAD = 2.3;

// Columns sit this much further apart than rows. Measured at 2.8:1, and it is
// what gives the field its combed texture rather than an even dot grid.
const COLUMN_RATIO = 2.8;

// How fast the field drifts along the noise's time axis, per millisecond.
// A mark takes about 30s to swing through its arc, so it breathes.
const DRIFT = 0.00003;

// Marks are seeded this far outside the canvas so the field bleeds off every edge.
const MARGIN = 80;

/**
 * The whole image is a pure function of (params, time): every frame clears and
 * redraws from scratch. That is why moving a slider looks instantaneous and why
 * the idle drift needs no extra machinery — there is only ever one code path.
 */
export default class FlowField {

  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    this.perlin = new Perlin();
    this.time = 0;
    this.frameId = null;

    this.params = { count: 2060, size: 47, flow: 240 };
    this.seeds = [];

    createControls(this.params, {
      onChange: () => this.render(),
      reseed: () => {
        this.perlin.seed();
        this.render();
      },
      download: () => this.download(),
    });

    this.watchViewport();
    this.watchVisibility();
  }

  // Narrow viewports get the portrait frame. Nothing measures the container, so
  // the composition only ever has two shapes and both are known up front.
  watchViewport() {
    const narrow = window.matchMedia(`(max-width: ${PORTRAIT_BELOW - 1}px)`);
    const apply = () => {
      const { width, height } = narrow.matches ? PORTRAIT : LANDSCAPE;
      if (this.canvas.width === width && this.canvas.height === height) return;
      this.canvas.width = width;
      this.canvas.height = height;
      this.seedCount = null;
      this.render();
    };
    narrow.addEventListener('change', apply);
    apply();
  }

  render() {
    const { ctx, canvas } = this;

    ctx.fillStyle = BACKGROUND;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Every mark shares one style, so the whole frame is a single path and a
    // single stroke() — the reason a full redraw fits in one frame budget.
    const path = new Path2D();
    this.seedPoints().forEach(seed => this.mark(path, seed.x, seed.y));

    ctx.strokeStyle = STROKE;
    ctx.lineWidth = STROKE_WIDTH;
    ctx.lineCap = 'round';
    ctx.stroke(path);
  }

  // One straight mark, `size` long, lying along the flow at this point. The
  // campaign marks measure straight to within a pixel, so a single segment it
  // is — the curve you read in the artwork comes from neighbouring marks
  // disagreeing, not from any one of them bending.
  mark(path, x, y) {
    const { size, flow } = this.params;
    const angle = this.perlin.get(x / flow, y / flow, this.time) * SPREAD;
    path.moveTo(x, y);
    path.lineTo(x + Math.cos(angle) * size, y + Math.sin(angle) * size);
  }

  // A regular grid, wider than it is tall, extended past every edge.
  seedPoints() {
    const { count } = this.params;
    if (count === this.seedCount) return this.seeds;

    // Solve for the row spacing that fits `count` marks at the fixed aspect.
    const { width, height } = this.canvas;
    const row = Math.sqrt((width * height) / (COLUMN_RATIO * count));
    const column = row * COLUMN_RATIO;

    this.seeds = [];
    for (let x = -MARGIN; x < width + MARGIN; x += column) {
      for (let y = -MARGIN; y < height + MARGIN; y += row) {
        this.seeds.push({ x, y });
      }
    }
    this.seedCount = count;
    return this.seeds;
  }

  start() {
    if (this.frameId || prefersReducedMotion()) return;
    const loop = now => {
      this.time = now * DRIFT;
      this.render();
      this.frameId = requestAnimationFrame(loop);
    };
    this.frameId = requestAnimationFrame(loop);
  }

  stop() {
    cancelAnimationFrame(this.frameId);
    this.frameId = null;
  }

  // No reason to burn frames on a canvas nobody is looking at.
  watchVisibility() {
    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.start();
      else this.stop();
    }).observe(this.canvas);
  }

  download() {
    const a = document.createElement('a');
    a.href = this.canvas.toDataURL('image/png');
    a.download = 'flow-field.png';
    a.click();
  }
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
